import { resumableUpload } from "./resumableUpload";

const SESSION_KEY = "pl-session-token-v2";

const getApiBaseUrl = () => {
    if (process.env.REACT_APP_PL_API_BASE) {
        return process.env.REACT_APP_PL_API_BASE;
    }

    const hostname = String(window.location.hostname || "").toLowerCase();
    if (hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1") {
        return `${window.location.protocol}//${hostname}:3002/api/pl`;
    }

    return "/api/pl";
};

const API_BASE_URL = getApiBaseUrl();
let visiblePostCount = 10;
const UPLOAD_INACTIVITY_TIMEOUT_MS = 90 * 1000;
const UPLOAD_RESPONSE_TIMEOUT_MS = 5 * 60 * 1000;

const getAssetBaseUrl = () => {
    if (process.env.REACT_APP_PL_ASSET_BASE) {
        return process.env.REACT_APP_PL_ASSET_BASE.replace(/\/$/, "");
    }

    const hostname = String(window.location.hostname || "").toLowerCase();
    if (hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1") {
        return `${window.location.protocol}//${hostname}:3002`;
    }

    return "";
};

const ASSET_BASE_URL = getAssetBaseUrl();

const resolveMediaUrl = (mediaUrl) => {
    if (!mediaUrl) {
        return null;
    }

    if (/^https?:\/\//i.test(mediaUrl)) {
        return mediaUrl;
    }

    return ASSET_BASE_URL ? `${ASSET_BASE_URL}${mediaUrl}` : mediaUrl;
};

const normalizePost = (post) => {
    if (!post || typeof post !== "object") {
        return post;
    }

    return {
        ...post,
        mediaUrl: resolveMediaUrl(post.mediaUrl),
        comments: Array.isArray(post.comments)
            ? post.comments.map((comment) => ({
                ...comment,
            }))
            : [],
    };
};

const getSessionToken = () => window.localStorage.getItem(SESSION_KEY) || "";

const setSessionToken = (token) => {
    if (token) {
        window.localStorage.setItem(SESSION_KEY, token);
        return;
    }

    window.localStorage.removeItem(SESSION_KEY);
};

const request = async (path, options = {}) => {
    const token = getSessionToken();
    const headers = new Headers(options.headers || {});

    if (token) {
        headers.set("Authorization", `Bearer ${token}`);
    }

    headers.set("X-PL-Post-Limit", String(visiblePostCount));

    const isFormData = typeof FormData !== "undefined" && options.body instanceof FormData;

    if (!isFormData && options.body && !headers.has("Content-Type")) {
        headers.set("Content-Type", "application/json");
    }

    const response = await fetch(`${API_BASE_URL}${path}`, {
        ...options,
        headers,
    });

    let payload = null;

    try {
        payload = await response.json();
    } catch (error) {
        payload = null;
    }

    if (!response.ok) {
        throw new Error((payload && payload.error) || "Request failed.");
    }

    return payload;
};

export const uploadRequest = ({ path, formData, onUploadProgress, method = "POST", contentType }) =>
    new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        let inactivityTimer = null;
        let abortMessage = "Upload stopped before it finished. Keep this page open and your device awake while uploading.";
        let lastUploadedBytes = 0;

        const clearInactivityTimer = () => {
            if (inactivityTimer) {
                window.clearTimeout(inactivityTimer);
                inactivityTimer = null;
            }
        };

        const startTimer = (duration, message) => {
            clearInactivityTimer();
            inactivityTimer = window.setTimeout(() => {
                // abort dispatches onabort synchronously, so set the reason first.
                abortMessage = message;
                xhr.abort();
                reject(new Error(message));
            }, duration);
        };
        const resetInactivityTimer = () => startTimer(
            UPLOAD_INACTIVITY_TIMEOUT_MS,
            "Upload timed out after 90 seconds without progress. Keep this page open and your device awake, and check your connection."
        );

        xhr.open(method, `${API_BASE_URL}${path}`);
        xhr.timeout = 0;

        const token = getSessionToken();
        if (token) {
            xhr.setRequestHeader("Authorization", `Bearer ${token}`);
        }

        xhr.setRequestHeader("X-PL-Post-Limit", String(visiblePostCount));
        if (contentType) xhr.setRequestHeader("Content-Type", contentType);

        xhr.upload.onprogress = (event) => {
            if (event.loaded > lastUploadedBytes) {
                lastUploadedBytes = event.loaded;
                resetInactivityTimer();
            }

            if (!onUploadProgress || !event.lengthComputable) {
                return;
            }

            onUploadProgress(Math.round((event.loaded / event.total) * 100));
        };

        xhr.upload.onload = () => startTimer(
            UPLOAD_RESPONSE_TIMEOUT_MS,
            "The file was sent, but the server did not confirm the post within 5 minutes. Check the feed before trying again."
        );

        xhr.onloadstart = resetInactivityTimer;

        xhr.onload = () => {
            clearInactivityTimer();
            let payload = null;

            try {
                payload = xhr.responseText ? JSON.parse(xhr.responseText) : null;
            } catch (error) {
                payload = null;
            }

            if (xhr.status < 200 || xhr.status >= 300) {
                const statusMessages = {
                    413: "The server or upload gateway rejected this file as too large (HTTP 413).",
                    408: "The server timed out while receiving the upload (HTTP 408).",
                    502: "The upload gateway could not reach the app (HTTP 502). Check the feed before trying again.",
                    504: "The upload gateway timed out waiting for the app (HTTP 504). Check the feed before trying again.",
                    524: "Cloudflare timed out waiting for the upload request to complete (HTTP 524). Check the feed before trying again.",
                };
                reject(Object.assign(new Error((payload && payload.error) || statusMessages[xhr.status] || `Upload request failed (HTTP ${xhr.status}).`), { status: xhr.status }));
                return;
            }

            resolve(payload);
        };

        xhr.onerror = () => {
            clearInactivityTimer();
            reject(new Error("The upload connection was lost. Keep this page open and your device awake, and check your connection."));
        };
        xhr.onabort = () => {
            clearInactivityTimer();
            reject(new Error(abortMessage));
        };
        xhr.send(formData);
    });

const normalizeState = (payload) => {
    const normalized = {};
    const source = payload && typeof payload === "object" ? payload : null;

    if (!source) {
        return normalized;
    }

    if (Object.prototype.hasOwnProperty.call(source, "user") || Object.prototype.hasOwnProperty.call(source, "currentUser")) {
        normalized.currentUser = source.user || source.currentUser || null;
    }

    if (Object.prototype.hasOwnProperty.call(source, "posts")) {
        normalized.posts = Array.isArray(source.posts) ? source.posts.map(normalizePost) : [];
    }

    if (Object.prototype.hasOwnProperty.call(source, "pagination")) {
        normalized.pagination = source.pagination || null;
    }

    if (Object.prototype.hasOwnProperty.call(source, "accessRequests") || Object.prototype.hasOwnProperty.call(source, "requests")) {
        normalized.accessRequests = source.accessRequests || source.requests || [];
    }

    return normalized;
};

export const setVisiblePlPostCount = (count) => {
    const normalized = Number.parseInt(count, 10);
    visiblePostCount = Number.isInteger(normalized) && normalized > 0 ? normalized : 10;
};

export const bootstrapPlApp = async ({ limit = 10 } = {}) => {
    const payload = await request(`/bootstrap?limit=${encodeURIComponent(limit)}`);
    return normalizeState(payload);
};

export const fetchPlPosts = async ({ offset = 0, limit = 10 } = {}) => {
    const payload = await request(`/posts?offset=${encodeURIComponent(offset)}&limit=${encodeURIComponent(limit)}`);
    return normalizeState(payload);
};

export const requestAccess = async ({ name, email, username, note }) =>
    request("/access-request", {
        method: "POST",
        body: JSON.stringify({ name, email, username, note }),
    });

export const loginWithPassword = async ({ identifier, password }) => {
    const payload = await request("/login", {
        method: "POST",
        body: JSON.stringify({ identity: identifier, password }),
    });

    setSessionToken(payload.sessionToken);
    return normalizeState(payload);
};

export const logoutPlUser = async () => {
    try {
        await request("/logout", {
            method: "POST",
        });
    } finally {
        setSessionToken("");
    }

    return {
        currentUser: null,
        posts: [],
        accessRequests: [],
    };
};

export const createPassword = async ({ token, password }) =>
    request("/create-password", {
        method: "POST",
        body: JSON.stringify({ token, password }),
    });

export const createPost = async ({ title, caption, file, userId, onUploadProgress, onUploadStatus }) => {
    const payload = await resumableUpload({ title, caption, file, userId, onUploadProgress, onUploadStatus,
        apiBase: API_BASE_URL, send: uploadRequest });
    return normalizeState(payload);
};

export const updatePost = async ({ postId, title, caption }) => {
    const payload = await request(`/posts/${postId}`, {
        method: "PUT",
        body: JSON.stringify({ title, caption }),
    });

    return normalizeState(payload);
};

export const saveComment = async ({ postId, commentId, body }) => {
    const payload = await request(
        commentId ? `/posts/${postId}/comments/${commentId}` : `/posts/${postId}/comments`,
        {
            method: commentId ? "PUT" : "POST",
            body: JSON.stringify({ body }),
        }
    );

    return normalizeState(payload);
};

export const deleteComment = async ({ postId, commentId }) => {
    const payload = await request(`/posts/${postId}/comments/${commentId}`, {
        method: "DELETE",
    });

    return normalizeState(payload);
};

export const toggleToastReaction = async ({ postId, type = "cheers" }) => {
    const payload = await request(`/posts/${postId}/reaction`, {
        method: "POST",
        body: JSON.stringify({ type }),
    });

    return normalizeState(payload);
};

export const toggleCommentReaction = async ({ postId, commentId, type = "cheers" }) => {
    const payload = await request(`/posts/${postId}/comments/${commentId}/reaction`, {
        method: "POST",
        body: JSON.stringify({ type }),
    });

    return normalizeState(payload);
};

export const deletePost = async ({ postId }) => {
    const payload = await request(`/posts/${postId}`, {
        method: "DELETE",
    });

    return normalizeState(payload);
};

export const deletePlProfile = async ({ deletePosts }) => {
    const payload = await request("/account", {
        method: "DELETE",
        body: JSON.stringify({ deletePosts }),
    });

    setSessionToken("");
    return normalizeState(payload);
};

export const updateNotificationPreferences = async ({ notifyNewPosts, notifyCommentsOnPosts }) => {
    const payload = await request("/account/preferences", {
        method: "PATCH",
        body: JSON.stringify({ notifyNewPosts, notifyCommentsOnPosts }),
    });

    return normalizeState(payload);
};

export const toggleUserSubscription = async ({ userId }) => {
    const payload = await request(`/users/${userId}/subscription`, {
        method: "POST",
    });

    return normalizeState(payload);
};

export const toggleThreadSubscription = async ({ postId }) => {
    const payload = await request(`/posts/${postId}/subscription`, {
        method: "POST",
    });

    return normalizeState(payload);
};

export const fetchAccessRequests = async () => {
    const payload = await request("/admin/requests");
    return normalizeState(payload);
};

export const decideAccessRequest = async ({ requestId, decision }) => {
    const payload = await request(`/admin/requests/${requestId}/decision`, {
        method: "POST",
        body: JSON.stringify({ decision }),
    });

    return {
        ...normalizeState(payload),
        setupToken: payload.setupToken,
        setupUrl: payload.setupUrl,
    };
};
